import BadgeComponent from './Badge'
import PanelComponent from './Panel'

function candleClose(c) {
  const n = parseFloat(c?.close)
  return Number.isFinite(n) ? n : null
}

function sma(data, period) {
  const out = []
  let sum = 0
  let validCount = 0
  for (let i = 0; i < data.length; i++) {
    const close = candleClose(data[i])
    if (close === null) {
      // Un dato inválido rompe la ventana: no podemos confiar en la SMA hasta
      // que se reconstruya una ventana completa de valores válidos.
      validCount = 0
      sum = 0
      out.push({ time: data[i].time, value: null })
      continue
    }
    sum += close
    validCount++
    if (i >= period) {
      const oldClose = candleClose(data[i - period])
      if (oldClose !== null) {
        sum -= oldClose
        validCount--
      }
    }
    if (validCount >= period) out.push({ time: data[i].time, value: sum / period })
    else out.push({ time: data[i].time, value: null })
  }
  return out
}

function stdDev(data, period) {
  const out = []
  for (let i = 0; i < data.length; i++) {
    if (i < period - 1) {
      out.push({ time: data[i].time, value: null })
      continue
    }
    let sum = 0
    let valid = 0
    for (let j = i - period + 1; j <= i; j++) {
      const close = candleClose(data[j])
      if (close === null) break
      sum += close
      valid++
    }
    if (valid < period) {
      out.push({ time: data[i].time, value: null })
      continue
    }
    const mean = sum / period
    let sq = 0
    for (let j = i - period + 1; j <= i; j++) {
      const close = candleClose(data[j])
      const d = close - mean
      sq += d * d
    }
    out.push({ time: data[i].time, value: Math.sqrt(sq / period) })
  }
  return out
}

function lineStyleValue(style) {
  switch (style) {
    case 'dotted': return 1
    case 'dashed': return 2
    case 'largeDashed': return 3
    case 'sparseDotted': return 4
    default: return 0
  }
}

export default {
  id: 'bollinger',
  name: 'Bollinger Bands',
  version: '1.0',

  defaultConfig: {
    period: 20,
    multiplier: 2,
    color: '#ec4899',
    lineWidth: 1.5,
    lineStyle: 'solid',
  },

  detect(candles, config) {
    if (!candles || candles.length === 0) return null
    const period = Math.max(2, Math.min(500, Math.round(config.period || 20)))
    const mult = parseFloat(config.multiplier ?? 2)
    if (!Number.isFinite(mult)) return null
    const ma = sma(candles, period)
    const sd = stdDev(candles, period)
    const out = []
    for (let i = 0; i < candles.length; i++) {
      const time = candles[i]?.time
      if (time == null) continue
      if (!Number.isFinite(ma[i]?.value) || !Number.isFinite(sd[i]?.value)) {
        out.push({ time, middle: null, upper: null, lower: null })
        continue
      }
      const m = ma[i].value
      const s = sd[i].value
      out.push({
        time,
        middle: m,
        upper: m + mult * s,
        lower: m - mult * s,
      })
    }
    return out
  },

  render(chart, candleSeries, result, config, api) {
    if (!result || result.length === 0) return
    const color = config.color
    const lineWidth = config.lineWidth ?? 1.5
    const lineStyle = lineStyleValue(config.lineStyle)

    const common = {
      color,
      lineWidth,
      lineStyle,
      lastValueVisible: false,
      priceLineVisible: false,
    }

    const middle = api.addSeries((chart) => chart.addLineSeries({ ...common, title: 'BB Mid' }))
    const upper = api.addSeries((chart) => chart.addLineSeries({ ...common, title: 'BB Upper' }))
    const lower = api.addSeries((chart) => chart.addLineSeries({ ...common, title: 'BB Lower' }))

    const toPoints = (key) => result
      .map(d => ({ time: d?.time, value: d?.[key] }))
      .filter(p => p.time != null && Number.isFinite(p.value))

    if (middle) middle.setData(toPoints('middle'))
    if (upper) upper.setData(toPoints('upper'))
    if (lower) lower.setData(toPoints('lower'))
  },

  BadgeComponent,
  PanelComponent,
}
